import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@tanstack/react-query';
import { union } from 'lodash';
import { WorksiteProps } from '@commutifi/models/Worksites';
import { RequestParams } from '@commutifi-fe/interfaces';
import { ExtractFnReturnType, MutationConfig, QueryConfig } from '../../reactQuery';
import { type GroupWriteBody, ExtendedGroupProps, GroupFormatter, deleteGroup, fetchEnterpriseGroups, fetchOneGroup, getPlansLinkedToGroup, postEnterpriseGroupsPlans, postGroup } from './api';
import { GroupProps } from '@commutifi/models/Groups';
import { PlanProps } from '@commutifi/models/Plan';
export const defaultGroupRelations = ['dynamicMemberships'];
export function useEnterpriseGroups(enterpriseId, t0, options) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    t2 = ["groups", enterpriseId, queryParams];
    t3 = async () => {
      if (!enterpriseId) {
        throw Error("Missing enterprise id");
      }
      const {
        relations,
        ...params
      } = queryParams;
      return fetchEnterpriseGroups(enterpriseId, {
        relations: relations ? union(defaultGroupRelations, relations) : defaultGroupRelations,
        ...params
      });
    };
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
}
export function useGroup(groupId, t0, options) {
  const $ = _c(12);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  const t2 = `groups/${groupId}`;
  let t3;
  if ($[2] !== queryParams || $[3] !== t2) {
    t3 = [t2, queryParams];
    $[2] = queryParams;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== groupId || $[6] !== queryParams) {
    t4 = async () => {
      if (!groupId) {}
      return fetchOneGroup(groupId || "", {
        relations: defaultGroupRelations,
        ...queryParams
      });
    };
    $[5] = groupId;
    $[6] = queryParams;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== options || $[9] !== t3 || $[10] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      ...options
    };
    $[8] = options;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return useQuery(t5);
}
export const usePlansLinkedToGroup = (groupId, t0, options) => {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== groupId || $[3] !== queryParams) {
    t2 = ["groups", groupId, "plans", queryParams];
    t3 = async () => getPlansLinkedToGroup(groupId, queryParams);
    $[2] = groupId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
};
export function useExtendedGroup(groupId, worksites, t0, t1) {
  const $ = _c(9);
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const queryParams = t2;
  let t3;
  if ($[2] !== t1) {
    t3 = t1 === undefined ? {} : t1;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const options = t3;
  let t4;
  if ($[4] !== worksites) {
    t4 = group => GroupFormatter.toExtendedGroupFormat(group, worksites);
    $[4] = worksites;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== options || $[7] !== t4) {
    t5 = {
      select: t4,
      ...options
    };
    $[6] = options;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return useGroup(groupId, queryParams, t5);
}
export const usePostGroup = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const usePostEnterpriseGroupsPlans = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp2,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useDeleteGroup = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp3,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
function _temp(body) {
  return postGroup(body);
}
function _temp2(body) {
  return postEnterpriseGroupsPlans(body);
}
function _temp3(groupId) {
  return deleteGroup(groupId);
}